import { Intent } from '@blueprintjs/core';
import * as go from 'gojs/release/go-module';
import isEqual from 'lodash.isequal';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';
import { SelectItem } from 'vet-bones/utils/select';

import { ColorPicker } from 'src/components/ColorPicker';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { ProjectDiagramClassDrawerRelationships } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationships';
import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { NodeCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { addAttributeItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import { addRelationshipItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptRelationshipForClass';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ProjectDiagramCreateEntityDrawer } from 'src/containers/ProjectDiagram/ProjectDiagramCreateEntityDrawer';
import { ModelConceptClassHierarchy } from 'src/containers/ProjectDiagram/types/data';
import { getColorIdForIri } from 'src/containers/ProjectDiagram/utils/colorIds';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { generateModelConceptIri } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/addModelConceptHierarchy';
import { rescaleDiagram } from 'src/containers/ProjectDiagram/utils/rescaleDiagram';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectSelector } from 'src/selectors/project';
import { projectClassIrisSelector } from 'src/selectors/projectClassIris';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramCreateClassDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const generateNewIri = useGenerateNewIri();
  const { projectId } = useRouterParams();

  const project = useReduxSelector(projectSelector(projectId));

  const { isOpen } = useReduxSelector((state) => state.ui.createClassDrawer);

  const allClassItems: SelectItem[] = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const existingClassIris = useReduxSelector(
    projectClassIrisSelector(projectId)
  );

  const [errorText, setErrorText] = React.useState({
    label: copy.components.projectDiagram.editableIri.validationLabelRequired,
    relationship: '',
  });
  const [iri, setIri] = React.useState('');
  const [colorId, setColorId] = React.useState(
    diagram?.model ? getColorIdForIri(diagram.model, iri) : ''
  );
  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange && isOpen) {
      setColorId(diagram?.model ? getColorIdForIri(diagram.model, iri) : '');
    }
  }, [didIsOpenChange, isOpen, diagram, iri]);

  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [didEditLabel, setDidEditLabel] = React.useState(false);

  const [parents, setParents] = React.useState<SelectItem[]>([]);
  const [children, setChildren] = React.useState<SelectItem[]>([]);

  const [relationships, setRelationships] = React.useState<
    ClassRelationshipItem[]
  >([]);

  const [attributes, setAttributes] = React.useState<AttributeTableItem[]>([]);

  const hierarchySelectItems = React.useMemo(() => {
    const parentIris = new Set(parents.map((item) => item.value));
    const childIris = new Set(children.map((item) => item.value));

    return {
      availableParents: allClassItems.filter(
        (item) => !parentIris.has(item.value)
      ),
      availableChildren: allClassItems.filter(
        (item) => !childIris.has(item.value)
      ),
    };
  }, [allClassItems, children, parents]);

  const allNewIris = React.useMemo(() => {
    const iris = new Set<string>();
    if (iri) {
      iris.add(iri);
    }
    parents.forEach((parent) => iris.add(parent.value));
    children.forEach((child) => iris.add(child.value));
    relationships.forEach((relationship) => {
      iris.add(relationship.value);
    });
    attributes.forEach((attribute) => {
      attribute.ranges.forEach((range) => iris.add(range.value));
    });
    return iris;
  }, [iri, parents, children, relationships, attributes]);

  const createNewClassFromQuery =
    useCreateModelConceptClassFromQuery(allNewIris);

  const handleUpdateIri = (iri: string, invalid: string) => {
    setIri(iri);
    setErrorText({ ...errorText, label: invalid });
  };
  const handleUpdateLabel = (label: string, invalid: string) => {
    setLabel(label);
    setDidEditLabel(true);
    setErrorText({ ...errorText, label: invalid });
    if (!label) {
      setIri('');
      return;
    }

    if (!project) {
      return;
    }

    const initialLabelIri = generateModelConceptIri(project, label.trim());
    const labelIri = generateNewIri(initialLabelIri);
    setIri(labelIri);
  };

  const handleAddParent = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setParents([...parents, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveParent = (selected: SelectItem) => {
    setParents(parents.filter((item) => item.value !== selected.value));
  };

  const handleAddChild = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setChildren([...children, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveChild = (selected: SelectItem) => {
    setChildren(children.filter((item) => item.value !== selected.value));
  };

  const handleAddRelationship = (relationship: ClassRelationshipItem) => {
    if (!relationship || (relationship.isNewlyCreated && !relationship.text)) {
      return;
    }
    setRelationships(
      [...relationships, relationship].sort(alphaSort((item) => item.text))
    );
  };

  const updateRelationshipError = (relationships: ClassRelationshipItem[]) => {
    const areThereDirectionlessRelationships = relationships.some(
      (rel) => !rel.directions.length
    );
    setErrorText({
      ...errorText,
      relationship: areThereDirectionlessRelationships
        ? copy.components.projectDiagram.classDrawer.relationshipsTable
            .missingDirection
        : '',
    });
  };

  const handleUnlinkRelationship = (relationship: ClassRelationshipItem) => {
    const updatedRelationships = relationships.filter(
      (item) => item.value !== relationship.value
    );
    setRelationships(updatedRelationships);
    updateRelationshipError(updatedRelationships);
  };

  const handleUpdateRelationship = (
    relationship: ClassRelationshipItem | null
  ) => {
    if (!relationship) {
      return;
    }

    const filtered = relationships.filter(
      (item) => item.value !== relationship.value
    );
    const updatedRelationships = [...filtered, relationship];
    setRelationships(updatedRelationships.sort(alphaSort((item) => item.text)));
    updateRelationshipError(updatedRelationships);
  };

  const handleAddAttribute = (attribute: AttributeTableItem) => {
    if (!attribute || (attribute.isNewlyCreated && !attribute.text)) {
      return;
    }
    setAttributes(
      [...attributes, attribute].sort(alphaSort((item) => item.text))
    );
  };

  const handleRemoveAttribute = (attribute: AttributeTableItem) => {
    if (!attribute) {
      return;
    }
    setAttributes(attributes.filter((item) => item.value !== attribute.value));
  };

  const handleCancel = () => {
    setErrorText({
      label: copy.components.projectDiagram.editableIri.validationLabelRequired,
      relationship: '',
    });
    setIri('');
    setLabel('');
    setDescription('');
    setDidEditLabel(false);
    setParents([]);
    setChildren([]);
    setRelationships([]);
    setAttributes([]);
    dispatch(
      uiSlice.actions.toggledCreateClassDrawer({
        isOpen: false,
      })
    );
  };

  const handleSubmit = () => {
    if (errorText.label || errorText.relationship || !diagram) {
      return;
    }

    commitDiagramModel(
      diagram,
      (model) => {
        addModelConceptClass({ iri, label, description, colorId })(model);

        parents.forEach((parent) => {
          if (!existingClassIris.has(parent.value)) {
            addModelConceptClass({ iri: parent.value, label: parent.text })(
              model
            );
          }
          addModelConceptHierarchy({ fromIri: iri, toIri: parent.value })(
            model
          );
        });

        children.forEach((child) => {
          if (!existingClassIris.has(child.value)) {
            addModelConceptClass({ iri: child.value, label: child.text })(
              model
            );
          }
          addModelConceptHierarchy({ fromIri: child.value, toIri: iri })(model);
        });

        relationships.forEach((relationship) => {
          if (relationship.isNewlyCreated && !relationship.text) {
            return;
          }
          addRelationshipItemToClass(iri, relationship)(model, diagram);
        });

        attributes.forEach((attribute) => {
          if (attribute.isNewlyCreated && !attribute.text) {
            return;
          }
          addAttributeItemToClass(iri, attribute)(model);
        });
      },
      { layout: DiagramModelCommitLayout.PARTIAL }
    );

    // since we don't want the partial relayout to happen
    // if we're ONLY changing the selection, we actually
    // need to put this separately
    commitDiagramWhileSkippingUndoManager(diagram, (_model, diagram) => {
      const classToSelect = diagram.findNodesByExample({
        value: iri,
        category: NodeCategory.MODEL_CONCEPT_CLASS,
      });

      // select the class that was just created
      if (classToSelect) {
        setDiagramSelection(new go.Set(classToSelect), true)(diagram);
      }
      rescaleDiagram(diagram);
    });

    handleCancel();
  };

  return (
    <ProjectDiagramCreateEntityDrawer
      disabled={Boolean(errorText.label || errorText.relationship)}
      errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
      errorText={errorText.label || errorText.relationship}
      isOpen={isOpen}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      title={copy.components.projectDiagram.classDrawer.createTitle}
    >
      <ProjectDiagramEditableIri
        errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
        header={copy.components.projectDiagram.classDrawer.name}
        iri={iri}
        label={label}
        onSetIri={handleUpdateIri}
        onSetLabel={handleUpdateLabel}
        placeholder={
          label === ''
            ? copy.components.projectDiagram.modelConceptClass.new
            : ''
        }
        rightEl={<ColorPicker colorId={colorId} onSetColorId={setColorId} />}
        setValueOnChange
        testId={TEST_IDS.projectDiagram.entityDrawer.name}
      />
      <ProjectDiagramEditableTextArea
        header={copy.components.projectDiagram.classDrawer.description}
        onSetValue={setDescription}
        value={description}
      />
      <div className="sd-drawer-project-diagram-entity-columns">
        <SelectableLinks
          buttonText=""
          creatable
          createNewItemFromQuery={createNewClassFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.classDrawer.parentClasses}
          items={hierarchySelectItems.availableParents}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(
              value,
              ModelConceptClassHierarchy.PARENTS,
              idx
            )
          }
          onRemove={handleRemoveParent}
          onSelect={handleAddParent}
          selected={parents}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
            ModelConceptClassHierarchy.PARENTS
          )}
        />
        <SelectableLinks
          buttonText=""
          creatable
          createNewItemFromQuery={createNewClassFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.classDrawer.childClasses}
          items={hierarchySelectItems.availableChildren}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(
              value,
              ModelConceptClassHierarchy.CHILDREN,
              idx
            )
          }
          onRemove={handleRemoveChild}
          onSelect={handleAddChild}
          selected={children}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
            ModelConceptClassHierarchy.CHILDREN
          )}
        />
      </div>
      <ProjectDiagramClassDrawerRelationships
        additionalIris={allNewIris}
        canUpdateDirectly
        onAddRelationship={handleAddRelationship}
        onUnlinkRelationship={handleUnlinkRelationship}
        onUpdateRelationship={handleUpdateRelationship}
        relationships={relationships}
      />
      <ProjectDiagramAttributesTable
        additionalIris={allNewIris}
        attributes={attributes}
        onAddAttribute={handleAddAttribute}
        onUnlinkAttribute={handleRemoveAttribute}
      />
    </ProjectDiagramCreateEntityDrawer>
  );
};
